const SCANNER_SECTOR_COLORS = {
  'Communication Services': 'bg-indigo-200 text-indigo-800',
  'Consumer Discretionary': 'bg-teal-200 text-teal-800',
  'Consumer Staples': 'bg-orange-200 text-orange-800',
  Energy: 'bg-yellow-200 text-yellow-800',
  Financials: 'bg-purple-200 text-purple-800',
  'Health Care': 'bg-green-200 text-green-800',
  Industrials: 'bg-slate-200 text-slate-800',
  'Information Technology': 'bg-blue-200 text-blue-800',
  Materials: 'bg-red-200 text-red-800',
  'Real Estate': 'bg-pink-200 text-pink-800',
  Utilities: 'bg-cyan-200 text-cyan-800',
} as const;

export const getSectorColor = (sector: string): string => {
  if (sector in SCANNER_SECTOR_COLORS) {
    return SCANNER_SECTOR_COLORS[sector as keyof typeof SCANNER_SECTOR_COLORS];
  }

  const normalized = sector.trim().toLowerCase();
  if (normalized.includes('technolog')) return SCANNER_SECTOR_COLORS['Information Technology'];
  if (normalized.includes('health')) return SCANNER_SECTOR_COLORS['Health Care'];
  if (normalized.includes('communication') || normalized.includes('telecom')) {
    return SCANNER_SECTOR_COLORS['Communication Services'];
  }
  if (normalized.includes('consumer discretionary') || normalized.includes('cyclical')) {
    return SCANNER_SECTOR_COLORS['Consumer Discretionary'];
  }
  if (normalized.includes('consumer staple') || normalized.includes('consumer defensive')) {
    return SCANNER_SECTOR_COLORS['Consumer Staples'];
  }
  if (normalized.includes('financ') || normalized.includes('insuranc')) {
    return SCANNER_SECTOR_COLORS.Financials;
  }
  if (normalized.includes('industri') || normalized.includes('manufactur')) {
    return SCANNER_SECTOR_COLORS.Industrials;
  }
  if (normalized.includes('energy')) return SCANNER_SECTOR_COLORS.Energy;
  if (normalized.includes('material') || normalized.includes('mining')) {
    return SCANNER_SECTOR_COLORS.Materials;
  }
  if (normalized.includes('utilit')) return SCANNER_SECTOR_COLORS.Utilities;
  if (normalized.includes('real estate') || normalized.includes('reit')) {
    return SCANNER_SECTOR_COLORS['Real Estate'];
  }

  return 'bg-gray-200 text-gray-800';
};

export const shortenChartLabel = (label: string): string => {
  const normalized = label.trim().toLowerCase();
  if (normalized.includes('technolog')) return 'Technology';
  if (normalized.includes('communication') || normalized.includes('telecom')) return 'Communications';
  if (normalized.includes('retail')) return 'Retail';
  if (normalized.includes('consumer')) return 'Consumer';
  if (normalized.includes('finance') || normalized.includes('financial') || normalized.includes('insuranc')) {
    return 'Financials';
  }
  if (normalized.includes('health')) return 'Healthcare';
  if (normalized.includes('industri') || normalized.includes('manufactur')) return 'Industrials';
  if (normalized.includes('transport')) return 'Transport';
  if (normalized.includes('energy')) return 'Energy';
  if (normalized.includes('utilit')) return 'Utilities';
  if (normalized.includes('real estate') || normalized.includes('reit')) return 'Real estate';
  if (normalized.includes('material') || normalized.includes('mining')) return 'Materials';
  if (normalized.includes('agricultur') || normalized.includes('forest')) return 'Agriculture';
  if (normalized.includes('service')) return 'Services';
  if (normalized.includes('united states') || normalized === 'usa' || normalized === 'us') return 'United States';
  if (normalized.includes('america')) return 'Americas';
  if (normalized.includes('europe')) return 'Europe';
  if (normalized.includes('asia')) return 'Asia';
  if (normalized.includes('oceania')) return 'Oceania';
  if (!label || normalized === 'unknown' || normalized === 'other') return 'Other';
  return label.length > 18 ? `${label.slice(0, 16)}…` : label;
};

const pieChartColors = ['#93c5fd', '#86efac', '#fde68a', '#d8b4fe', '#fda4af', '#7dd3fc', '#bef264', '#fdba74'];

export const generatePieChartOptions = ({
  data,
  name,
  watchlistName,
}: {
  data: string[];
  name: string;
  watchlistName: string;
}) => {
  const counts = data.reduce<Record<string, number>>((agg, value) => {
    const group = shortenChartLabel(value || 'Other');
    agg[group] = (agg[group] ?? 0) + 1;
    return agg;
  }, {});

  const breakdown = Object.entries(counts)
    .map(([label, value]) => ({ name: label, value }))
    .sort((first, second) => second.value - first.value)
    .map((item, index) => ({
      ...item,
      color: pieChartColors[index % pieChartColors.length],
    }));

  return {
    breakdown,
    name,
    option: {
      animation: true,
      backgroundColor: 'transparent',
      color: pieChartColors,
      legend: {
        show: false,
      },
      series: [
        {
          name: watchlistName,
          type: 'pie',
          radius: ['50%', '82%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderColor: '#1e3a8a',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          emphasis: {
            scale: true,
            scaleSize: 4,
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.35)',
            },
          },
          data: breakdown.map(({ name: sliceName, value }) => ({ name: sliceName, value })),
        },
      ],
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderWidth: 0,
        textStyle: {
          color: '#ffffff',
        },
      },
    },
  };
};

export type WatchlistMixConfig = ReturnType<typeof generatePieChartOptions>;

export const buildWatchlistMixConfigs = ({
  regions,
  sectors,
  watchlistName,
}: {
  regions: string[];
  sectors: string[];
  watchlistName: string;
}): WatchlistMixConfig[] => [
  generatePieChartOptions({ data: sectors, name: 'Sectors', watchlistName }),
  generatePieChartOptions({ data: regions, name: 'Regions', watchlistName }),
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatHistoryChartMonthLabel = (date: string): string => {
  const year = Number(date.slice(0, 4));
  const monthIndex = Number(date.slice(5, 7)) - 1;
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) return date;
  return `${MONTH_LABELS[monthIndex]} ${String(year).slice(2)}`;
};

export const toMonthKey = (date: string): string => {
  if (date.length < 7) return date;
  return `${date.slice(0, 7)}-01`;
};

export const fillMonthlySeriesValues = (
  dates: string[],
  historical: Record<string, number>,
  fallback = 0,
): number[] => {
  let lastKnown: number | null = null;

  return dates.map(date => {
    const value = historical[date];
    if (Number.isFinite(value)) {
      lastKnown = value;
      return Math.round(value * 100) / 100;
    }

    const filled = lastKnown ?? fallback;
    return Math.round(filled * 100) / 100;
  });
};

export const generateHistoryLineChartOptions = ({ dates, data }: { dates: string[]; data: string[] }) => {
  const labelStep = Math.max(1, Math.ceil(dates.length / 8));

  const shouldShowAxisLabel = (index: number): boolean => {
    if (dates.length <= 1) return index === 0;
    if (index === 0) return false;
    if (index === dates.length - 1) return true;
    return (index - 1) % labelStep === 0;
  };

  const lineChartOptions = {
    animation: false,
    backgroundColor: '#2e79f6',
    color: [
      '#00d8ff',
      '#00c2ed',
      '#00acda',
      '#0397c6',
      '#0b83b1',
      '#106f9d',
      '#125c88',
      '#114972',
      '#0e385e',
      '#0a2749',
    ],
    tooltip: {
      trigger: 'axis',
      className: 'ticker-line-chart-format',
      axisPointer: {
        type: 'cross',
        label: {
          precision: '2',
          formatter: (params: { axisDimension?: string; value?: string | number }) => {
            if (params.axisDimension === 'x' && typeof params.value === 'string') {
              return formatHistoryChartMonthLabel(params.value);
            }
            if (typeof params.value === 'number') {
              return params.value.toFixed(2);
            }
            return String(params.value ?? '');
          },
        },
        crossStyle: {
          color: '#FFF',
        },
      },
    },
    grid: {
      left: 0,
      right: 0,
      bottom: 22,
      top: 8,
      containLabel: false,
    },
    xAxis: [
      {
        show: true,
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          show: true,
          color: 'rgba(255, 255, 255, 0.78)',
          fontSize: 11,
          interval: (index: number) => shouldShowAxisLabel(index),
          hideOverlap: true,
          margin: 8,
          alignMaxLabel: 'right',
          formatter: (value: string) => formatHistoryChartMonthLabel(value),
        },
      },
    ],
    yAxis: [
      {
        show: true,
        type: 'value',
        axisLabel: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#2e79f6',
          },
        },
      },
    ],
    axisLine: {
      show: true,
    },
    series: [
      {
        name: 'Total',
        type: 'line',
        stack: 'Total',
        showSymbol: false,
        symbol: 'none',
        areaStyle: {
          opacity: 1,
        },
        emphasis: {
          focus: 'series',
          scale: false,
        },
        data: data,
      },
    ],
  };

  return lineChartOptions;
};
